import { Link, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { useAuthStore } from '@/stores/auth';
import { useUIStore } from '@/stores/ui';
import { Button } from '@/components/common/Button';
import { BasketIcon } from '@/components/merchandise/BasketIcon';

const publicNavItems = [
  { label: 'Home', path: '/' },
  { label: 'Events', path: '/events' },
  { label: 'Membership', path: '/membership' },
  { label: 'Articles', path: '/articles' },
  { label: 'Sponsors', path: '/sponsors' },
  { label: 'About', path: '/about' },
  { label: 'Contact', path: '/contact' },
];

export function Header() {
  const location = useLocation();
  const { isAuthenticated, user, logout } = useAuthStore();
  const { openMobileMenu } = useUIStore();

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    // Dashboard (/members) should only highlight on exact match
    if (path === '/members') {
      return location.pathname === '/members';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Skip to content link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-0 focus:left-0 focus:z-50 focus:px-4 focus:py-2 focus:bg-pce-gold focus:text-black focus:font-medium"
      >
        Skip to main content
      </a>
      <header className="sticky top-0 z-40 w-full bg-pce-black border-b border-gray-800">
        <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" aria-label="Performance Car Exclusive - Home">
            <img
              src="/img/logo.webp"
              alt="Performance Car Exclusive"
              className="h-10 w-auto"
              width="160"
              height="40"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1" aria-label="Main navigation">
            {publicNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={clsx(
                  'px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                  isActive(item.path)
                    ? 'text-gold'
                    : 'text-gray-300 hover:text-white hover:bg-gray-800'
                )}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Desktop Auth Section */}
          <div className="hidden lg:flex items-center gap-4">
            {isAuthenticated ? (
              <>
                <Link
                  to="/members"
                  className="text-sm font-medium text-gray-300 hover:text-white transition-colors"
                >
                  Dashboard
                </Link>
                {user?.roles?.includes('Administrator') && (
                  <Link
                    to="/admin"
                    className="text-sm font-medium text-amber-500 hover:text-amber-400 transition-colors"
                  >
                    Admin
                  </Link>
                )}
                {/* Merchandise basket icon — only shown when authenticated */}
                <BasketIcon />
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-400">
                    {user?.firstName}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => logout()}
                  >
                    Logout
                  </Button>
                </div>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" size="sm">
                    Login
                  </Button>
                </Link>
                <Link to="/apply">
                  <Button size="sm">Join PCE</Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            type="button"
            className="lg:hidden p-2 text-gray-300 hover:text-white"
            onClick={openMobileMenu}
            aria-label="Open menu"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </div>
        </div>
      </header>
    </>
  );
}
